import * as Vue from 'vue' // eslint-disable-next-line
import { getRouters } from '@/api/menu'
import { indexRouterMap } from '@/config/router.config'
import allIcon from '@/core/icons'
import { validURL } from '@/utils/validate'
import { UserLayout, BlankLayout } from '@/layouts'
import PageView from '@/layouts/PageView.vue'
import auth from '@/plugins/auth'

// 使用 require.context 来动态加载所有 views 下的组件
// 这需要在文件顶部定义，不能在函数内部动态创建
const viewsContext = require.context('@/views', true, /\.vue$/)

// 前端路由表
const constantRouterComponents = {
  // 基础页面 layout 必须引入
  BasicLayout: () => import('@/layouts/BasicLayout'),
  BlankLayout: BlankLayout,
  RouteView: () => import('@/layouts/RouteView'),
  PageView: PageView,
  UserLayout: UserLayout, // 登陆注册页面的通用布局

  // 你需要动态引入的页面组件
  Index: () => import('@/views/index'),
  // account
  AccountCenter: () => import('@/views/account/center'),
  AccountSettings: () => import('@/views/account/settings'),
  // 授权用户
  AuthUser: () => import('@/views/system/role/authUser'),
  // 公告新增修改
  NoticeForm: () => import('@/views/system/notice/modules/CreateForm'),
  //
  zrIndex: () => import('@/views/sms/channel/zrdata'),
  // 修改生成配置
  GenEdit: () => import('@/views/tool/gen/modules/GenEdit'),
}

// 前端未找到页面路由（固定不用改）
const notFoundRouter = {
  path: '*',
  redirect: '/404',
  hidden: true,
}

// 根级菜单
const rootMenu = {
  key: '',
  name: 'AppLayout', // 使用更唯一的名称避免与子路由冲突
  path: '',
  component: 'BasicLayout',
  redirect: '/index',
  meta: {
    title: '首页',
  },
  children: [],
}

// 根级路由
const rootRouter = {
  key: '',
  name: 'AppLayout', // 使用更唯一的名称避免与子路由冲突
  path: '',
  component: constantRouterComponents.BasicLayout,
  redirect: '/index',
  meta: {
    title: '首页',
  },
  children: [],
}

/**
 * 动态生成菜单
 * @param token
 * @returns {Promise<Router>}
 */
export const generatorDynamicRouter = (token) => {
  return new Promise((resolve, reject) => {
    // 向后端请求路由数据
    getRouters()
      .then((res) => {
        // 路由菜单分离，路由全部为二级，解决多级菜单缓存问题
        const routers = []
        const menuNav = []
        const routerData = res.data
        const asyncRoutes = filterDynamicRoutes(indexRouterMap)
        rootMenu.children = asyncRoutes.concat(routerData)
        menuNav.push(rootMenu)
        const menus = generator(menuNav, null, routers)
        menus.push(notFoundRouter)
        rootRouter.children = routers
        
        // 调试：检查路由结构
        console.log('generatorDynamicRouter: routers 数组长度', routers.length)
        console.log('generatorDynamicRouter: rootRouter.children 长度', rootRouter.children?.length)
        if (routers.length > 0) {
          const projectUserRoute = routers.find(r => r.path && r.path.includes('projectUser'))
          if (projectUserRoute) {
            console.log('generatorDynamicRouter: 找到 projectUser 路由在 routers 数组中:', {
              path: projectUserRoute.path,
              name: projectUserRoute.name,
              component: projectUserRoute.component,
              componentType: typeof projectUserRoute.component
            })
          }
          
          // 检查 /index 路由
          const indexRoute = routers.find(r => r.path === '/index' || r.name === 'Index')
          if (indexRoute) {
            console.log('generatorDynamicRouter: 找到 /index 路由在 routers 数组中:', {
              path: indexRoute.path,
              name: indexRoute.name,
              componentType: typeof indexRoute.component,
              component: indexRoute.component
            })
          } else {
            console.warn('generatorDynamicRouter: 未找到 /index 路由在 routers 数组中')
            console.log('generatorDynamicRouter: routers 数组中的路由路径（前10个）:', routers.map(r => ({ path: r.path, name: r.name })).slice(0, 10))
          }
        }
        if (rootRouter.children && rootRouter.children.length > 0) {
          const projectUserRouteInChildren = rootRouter.children.find(r => r.path && r.path.includes('projectUser'))
          if (projectUserRouteInChildren) {
            console.log('generatorDynamicRouter: 找到 projectUser 路由在 rootRouter.children 中:', {
              path: projectUserRouteInChildren.path,
              name: projectUserRouteInChildren.name,
              component: projectUserRouteInChildren.component,
              componentType: typeof projectUserRouteInChildren.component
            })
          }
          
          // 检查 /index 路由
          const indexRouteInChildren = rootRouter.children.find(r => r.path === '/index' || r.name === 'Index')
          if (indexRouteInChildren) {
            console.log('generatorDynamicRouter: 找到 /index 路由在 rootRouter.children 中:', {
              path: indexRouteInChildren.path,
              name: indexRouteInChildren.name,
              componentType: typeof indexRouteInChildren.component,
              component: indexRouteInChildren.component
            })
          } else {
            console.warn('generatorDynamicRouter: 未找到 /index 路由在 rootRouter.children 中')
            console.log('generatorDynamicRouter: rootRouter.children 中的路由路径（前10个）:', rootRouter.children.map(r => ({ path: r.path, name: r.name })).slice(0, 10))
          }
        }
        
        resolve({ menus, routers: rootRouter })
      })
      .catch((err) => {
        reject(err)
      })
  })
}

// 动态路由遍历，验证是否具备权限
export function filterDynamicRoutes(routes) {
  const res = []
  routes.forEach((route) => {
    if (route.permissions) {
      if (auth.hasPermiOr(route.permissions)) {
        res.push(route)
      }
    } else if (route.roles) {
      if (auth.hasRoleOr(route.roles)) {
        res.push(route)
      }
    } else {
      res.push(route)
    }
  })
  return res
}

/**
 * 格式化树形结构数据 生成 vue-router 层级路由表
 *
 * @param routerMap
 * @param parent
 * @param routers
 * @returns {*}
 */
export const generator = (routerMap, parent, routers) => {
  const names = parent ? parent.meta.names : []
  return routerMap.map((item) => {
    // 适配ruoyi一级菜单
    if (item.path === '/' && item.children && item.children.length === 1) {
      item = item.children[0]
      item.children = undefined
    }

    const {
      title,
      show,
      hideChildren,
      hiddenHeaderContent,
      hidden,
      icon,
      noCache,
    } = item.meta || {}
    if (item.isFrame === 0) {
      item.target = '_blank'
    }
    let name = item.name || item.key || ''
    // 确保路由名称唯一，如果父路由名称是 'AppLayout' 或 'root'，子路由名称不能重复
    if (parent && (parent.name === 'AppLayout' || parent.name === 'root') && (name === 'root' || name === 'AppLayout')) {
      // 如果子路由名称与父路由冲突，使用路径生成更合理的名称
      if (item.path) {
        // 使用路径生成名称，例如：/xiangmuguanli/projectUser -> xiangmuguanli_projectUser
        name = item.path
          .replace(/^\//, '') // 移除开头的斜杠
          .replace(/\//g, '_') // 将斜杠替换为下划线
          .replace(/_+/g, '_') // 将多个下划线替换为单个
          .replace(/^_|_$/g, '') // 移除开头和结尾的下划线
        if (!name) {
          name = 'child'
        }
      } else {
        name = 'child'
      }
      console.warn(`generator: 子路由名称冲突，将 ${item.name || item.key} 改为 ${name}`, {
        originalName: item.name || item.key,
        newName: name,
        path: item.path
      })
    }
    // 如果 name 为空，使用 path 生成一个唯一的 name
    if (!name && item.path) {
      name = item.path.replace(/\//g, '_').replace(/^_/, '').replace(/_/g, '_') || 'route'
    }
    const isRouter =
      item.component &&
      item.component !== 'Layout' &&
      item.component !== 'ParentView'
    // 生成路由路径，确保以 "/" 开头
    let routePath = item.path
    if (!routePath) {
      // 如果没有 path，从父路径和 item.path 拼接
      const parentPath = (parent && parent.path) || ''
      routePath = parentPath ? `${parentPath}/${item.path || ''}` : `/${item.path || ''}`
    } else if (!routePath.startsWith('/')) {
      // 如果 path 存在但不以 "/" 开头，需要根据是否有父路径来决定
      if (parent && parent.path) {
        routePath = `${parent.path}/${routePath}`
      } else {
        routePath = `/${routePath}`
      }
    } else {
      // path 已经以 "/" 开头，但如果有父路径且不是根路径，可能需要调整
      if (parent && parent.path && parent.path !== '/' && !routePath.startsWith(parent.path)) {
        // 确保父路径和子路径之间只有一个斜杠
        const parentPathNormalized = parent.path.endsWith('/') ? parent.path.slice(0, -1) : parent.path
        const childPathNormalized = routePath.startsWith('/') ? routePath : `/${routePath}`
        routePath = `${parentPathNormalized}${childPathNormalized}`
      }
    }
    
    // 确保路径以 "/" 开头
    if (!routePath.startsWith('/')) {
      routePath = '/' + routePath
    }
    
    // 修复双斜杠问题：将连续的斜杠替换为单个斜杠
    routePath = routePath.replace(/\/+/g, '/')
    
    // 调试：打印路由生成信息
    if (routePath && routePath.includes('projectUser')) {
      console.log('生成 projectUser 路由:', {
        routePath,
        name,
        component: item.component,
        item
      })
    }
    
    const currentRouter = {
      // 如果路由设置了 path，则作为默认 path，否则 路由地址 动态拼接生成如 /dashboard/workplace
      path: routePath,
      // 路由名称，建议唯一
      name: name,
      // 该路由对应页面的 组件(动态加载)
      component:
        constantRouterComponents[item.component || item.key] ||
        (() => {
          // webpack 的动态导入（使用模板字符串）无法正确解析 @/ 别名
          // 使用 require.context 来动态加载组件（已在文件顶部创建 viewsContext）
          const componentPath = item.component
          console.log('动态加载组件:', componentPath, '路由路径:', routePath)
          
          // 构建完整的组件路径（相对于 @/views，需要以 ./ 开头）
          // 例如：system/projectUser/index -> ./system/projectUser/index.vue
          let fullPath = `./${componentPath}`
          // 确保路径以 .vue 结尾
          if (!fullPath.endsWith('.vue')) {
            fullPath = `${fullPath}.vue`
          }
          
          console.log('尝试加载组件，路径:', fullPath)
          
          // Vue Router 需要的是一个返回 Promise 的函数
          // require.context 是同步的，但我们需要返回一个 Promise
          return new Promise((resolve, reject) => {
            try {
              // require.context 返回的函数需要传入一个路径来获取模块
              // 路径必须是 context 能够找到的路径
              const module = viewsContext(fullPath)
              if (module) {
                // 模块可能导出 default 或者直接导出
                const component = module.default || module
                console.log('组件加载成功:', fullPath, 'component type:', typeof component)
                resolve(component)
              } else {
                // 如果 require.context 找不到，尝试使用动态 import
                console.warn('require.context 找不到模块，尝试动态 import:', fullPath)
                import(`@/views/${componentPath}`)
                  .then(importedModule => {
                    const component = importedModule.default || importedModule
                    console.log('动态 import 成功:', componentPath)
                    resolve(component)
                  })
                  .catch(importErr => {
                    console.error('动态 import 也失败:', importErr)
                    reject(new Error(`Cannot find module '@/views/${componentPath}'. 请检查组件路径是否正确。`))
                  })
              }
            } catch (err) {
              console.error('require.context 加载失败:', fullPath, err)
              // 如果 require.context 失败，尝试使用动态 import
              import(`@/views/${componentPath}`)
                .then(importedModule => {
                  const component = importedModule.default || importedModule
                  console.log('动态 import 成功（fallback）:', componentPath)
                  resolve(component)
                })
                .catch(importErr => {
                  console.error('动态 import 也失败:', importErr)
                  reject(new Error(`Cannot find module '@/views/${componentPath}'. 请检查组件路径是否正确。`))
                })
            }
          })
        }),
      hidden: item.hidden,
      // meta: 页面标题, 菜单图标, 页面权限(供指令权限用，可去掉)
      meta: {
        title: title,
        icon: allIcon[icon + 'Icon'] || icon,
        hiddenHeaderContent: hiddenHeaderContent,
        // 目前只能通过判断path的http链接来判断是否外链，适配若依
        target: validURL(item.path) ? '_blank' : '',
        permission: item.name,
        keepAlive: noCache === undefined ? false : !noCache,
        hidden: hidden,
        // 因菜单路由分离，通过此names确定菜单树的展开
        names: names.concat([name]),
      },
      redirect: item.redirect,
    }
    // 是否设置了隐藏菜单
    if (show === false) {
      currentRouter.hidden = true
    }
    // 是否设置了隐藏子菜单
    if (hideChildren) {
      currentRouter.hideChildrenInMenu = true
    }
    // const names = parent.names
    // 确保最终路由路径以 "/" 开头
    let finalPath = currentRouter.path
    if (finalPath && !finalPath.startsWith('/')) {
      finalPath = '/' + finalPath
    }
    
    // 确保 component 是一个函数（异步组件）
    let component = currentRouter.component
    if (typeof component === 'function') {
      // 如果是函数，包装它以便添加调试信息
      const originalComponent = component
      component = () => {
        console.log('加载路由组件:', finalPath, 'name:', currentRouter.name)
        return originalComponent().then(module => {
          console.log('路由组件加载成功:', finalPath, 'module:', module)
          return module
        }).catch(error => {
          console.error('路由组件加载失败:', finalPath, 'error:', error)
          throw error
        })
      }
    }
    
    const router = {
      path: finalPath,
      // 路由名称，建议唯一
      name: currentRouter.name,
      // 该路由对应页面的 组件(动态加载)
      component: component,
      hidden: currentRouter.hidden,
      // meta: 页面标题, 菜单图标, 页面权限(供指令权限用，可去掉)
      meta: currentRouter.meta,
      redirect: currentRouter.redirect,
    }
    if (router.component && isRouter) {
      // 调试：打印路由信息
      if (finalPath && finalPath.includes('projectUser')) {
        console.log('添加路由:', {
          path: router.path,
          name: router.name,
          component: router.component,
          meta: router.meta
        })
      }
      // 调试：检查 /index 路由
      if (finalPath === '/index' || router.name === 'Index') {
        console.log('添加 /index 路由:', {
          path: router.path,
          name: router.name,
          component: router.component,
          componentType: typeof router.component,
          isRouter: isRouter
        })
      }
      routers.push(router)
    } else {
      // 调试：检查为什么 /index 路由没有被添加
      if (finalPath === '/index' || router.name === 'Index') {
        console.warn(' /index 路由未被添加到 routers 数组:', {
          path: router.path,
          name: router.name,
          component: router.component,
          componentType: typeof router.component,
          isRouter: isRouter,
          hasComponent: !!router.component
        })
      }
    }
    // 是否有子菜单，并递归处理，并将父path传入
    if (item.children && item.children.length > 0) {
      // Recursion
      currentRouter.children = generator(item.children, currentRouter, routers)
    }
    return currentRouter
  })
}
